import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';

import { Store } from '@ngrx/store';

import { Observable, Subscription } from 'rxjs';

import { ToastrService } from 'ngx-toastr';

import { removeWhitespaces } from 'src/app/core/custom-form-validators';
import { setSelectedLanguage, updateStudy } from 'src/app/dashboard/state/study/study.actions';
import {
  selectSelectedLanguage,
  selectSelectedStudy,
  selectSupportedLanguages,
} from 'src/app/dashboard/state/study/study.selectors';
import { Language } from 'src/app/models/language';
import { Study } from 'src/app/models/study';
import { StudyType } from 'src/app/models/study-type';
import { tinymceInitConfig } from 'src/app/shared/components/tinymce-editor/tinymce-editor-config';
import { AppState } from 'src/app/state';
import { isThereEmptyTranslation } from '../../helpers/translation-helper';
import { STUDY_DESCRIPTION_IDENTIFIER, STUDY_TITLE_IDENTIFIER } from '../study-details/study-details.component';

export enum EMAIL_PAGE_IDENTIFIERS {
  MAIL_WELCOME_SUBJECT = 'mail_welcome_subject',
  MAIL_WELCOME = 'mail_welcome',
  MAIL_REJECTED_SUBJECT = 'mail_rejected_subject',
  MAIL_REJECTED = 'mail_rejected',
  MAIL_OUTCOMES_SUBJECT = 'mail_outcomes_subject',
  MAIL_OUTCOMES_INTRO = 'mail_outcomes_intro',
  MAIL_OUTCOMES_OUTRO = 'mail_outcomes_outro',
}

@Component({
  selector: 'dashboard-email-pages',
  templateUrl: './email-pages.component.html',
  styleUrls: ['./email-pages.component.scss'],
})
export class EmailPagesComponent implements OnInit, OnDestroy {
  supportedLanguages$: Observable<Language[]>;
  supportedLanguages: Language[];
  selectedLanguage$: Observable<Language>;
  selectedLanguage: Language;
  selectedStudy$: Observable<Study>;
  selectedStudy: Study;

  languageTabsEditor: MatTabGroup;
  emailPageForm: FormGroup;

  StudyType = StudyType;
  EMAIL_PAGE_IDENTIFIERS = EMAIL_PAGE_IDENTIFIERS;

  isEdit = false;
  tinymceInitConfig = {
    ...tinymceInitConfig,
    height: 380,
  };

  private subscriptions$ = new Subscription();

  constructor(
    private store: Store<AppState>,
    private formBuilder: FormBuilder,
    private toastrService: ToastrService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.emailPageForm = this.formBuilder.group({
      emailTranslations: this.formBuilder.array([]),
    });

    this.selectedStudy$ = this.store.select(selectSelectedStudy);
    this.subscriptions$.add(
      this.selectedStudy$.subscribe((selectedStudy) => {
        this.selectedStudy = selectedStudy;
        this.isEdit = selectedStudy?.translations?.some(
          (translation) => translation.identifier.toUpperCase() in EMAIL_PAGE_IDENTIFIERS
        );
      })
    );

    this.selectedLanguage$ = this.store.select(selectSelectedLanguage);
    this.subscriptions$.add(
      this.selectedLanguage$.subscribe((language) => {
        this.selectedLanguage = language;
        this.changeDetectorRef.detectChanges();
      })
    );

    this.supportedLanguages$ = this.store.select(selectSupportedLanguages);
    this.subscriptions$.add(
      this.supportedLanguages$.subscribe((languages) => {
        this.supportedLanguages = languages;
        this.emailTranslations.clear();
        if (this.supportedLanguages) {
          this.supportedLanguages.forEach((language) => this.createTranslationFields(language?.code));
        } else {
          this.createTranslationFields(this.selectedLanguage.code);
        }
      })
    );
  }

  get emailTranslations(): FormArray {
    return this.emailPageForm.get('emailTranslations') as FormArray;
  }

  createTranslationFields(languageCode): void {
    const filteredTranslations = this.selectedStudy?.translations?.filter(
      (translation) =>
        translation?.language === languageCode &&
        translation?.identifier !== STUDY_TITLE_IDENTIFIER &&
        translation?.identifier !== STUDY_DESCRIPTION_IDENTIFIER
    );

    filteredTranslations?.forEach((filteredTranslation) =>
      this.emailTranslations.push(
        this.formBuilder.group({
          id: [filteredTranslation?.id || null],
          language: [languageCode],
          identifier: [filteredTranslation?.identifier || null],
          translation: [filteredTranslation?.translation || null, [Validators.required, removeWhitespaces]],
        })
      )
    );

    if (!this.isEdit || (this.isEdit && filteredTranslations?.length === 0)) {
      if (this.selectedStudy?.type !== StudyType.CC) {
        this.pushEmptyTranslation(languageCode, EMAIL_PAGE_IDENTIFIERS.MAIL_REJECTED);
        this.pushEmptyTranslation(languageCode, EMAIL_PAGE_IDENTIFIERS.MAIL_REJECTED_SUBJECT);
        this.pushEmptyTranslation(languageCode, EMAIL_PAGE_IDENTIFIERS.MAIL_WELCOME);
        this.pushEmptyTranslation(languageCode, EMAIL_PAGE_IDENTIFIERS.MAIL_WELCOME_SUBJECT);
      } else {
        this.pushEmptyTranslation(languageCode, EMAIL_PAGE_IDENTIFIERS.MAIL_OUTCOMES_SUBJECT);
        this.pushEmptyTranslation(languageCode, EMAIL_PAGE_IDENTIFIERS.MAIL_OUTCOMES_INTRO);
        this.pushEmptyTranslation(languageCode, EMAIL_PAGE_IDENTIFIERS.MAIL_OUTCOMES_OUTRO);
      }
    }
  }

  pushEmptyTranslation(languageCode: string, id: EMAIL_PAGE_IDENTIFIERS) {
    this.emailTranslations.push(
      this.formBuilder.group({
        id: [null],
        language: [languageCode],
        identifier: [id],
        translation: [null, [Validators.required, removeWhitespaces]],
      })
    );
  }

  selectTemplateLanguage(event): void {
    const foundSelected = this.supportedLanguages.find((language) => language?.name === event.tab.textLabel);
    if (this.selectedLanguage !== foundSelected) {
      this.store.dispatch(setSelectedLanguage({ selectedLanguage: foundSelected }));
    }
  }

  selectFirstTab() {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
  }

  savePage(): void {
    if (isThereEmptyTranslation(this.emailTranslations.value)) {
      this.toastrService.error('Please populate Title and Content in all selected languages');
      return;
    }

    if (this.emailPageForm.invalid) {
      return;
    }

    const studyDetails = {
      id: this.selectedStudy.id,
      languages: this.selectedStudy.languages.toString(),
      emailRequired: this.selectedStudy.emailRequired,
      translations: this.emailTranslations.value,
    } as Study & { languages: string };

    this.store.dispatch(updateStudy({ study: studyDetails }));
  }

  ngOnDestroy(): void {
    this.subscriptions$.unsubscribe();
  }
}
